import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FinancialStateProvider } from "@/context/StateContext";
import { LifeStateProvider } from "@/context/LifeStateContext";
import { WellnessStateProvider } from "@/context/WellnessStateContext";
import { PeriodProvider } from "@/context/PeriodContext";
import Layout from "@/components/Layout";
import LifeLayout from "@/components/LifeLayout";
import WellnessLayout from "@/components/WellnessLayout";
import Login from "@/pages/Login";
import CommandSelect from "@/pages/CommandSelect";
import Overview from "@/pages/Overview";
import CashFlow from "@/pages/CashFlow";
import Budget from "@/pages/Budget";
import LifePlan from "@/pages/LifePlan";
import Credit from "@/pages/Credit";
import Accounts from "@/pages/Accounts";
import Goals from "@/pages/Goals";
import PurchasePlanner from "@/pages/PurchasePlanner";
import Grocery from "@/pages/Grocery";
import Advisor from "@/pages/Advisor";
import WealthPlan from "@/pages/WealthPlan";
import Protection from "@/pages/Protection";
import Settings from "@/pages/Settings";
import LifeCommand from "@/pages/life/Command";
import LifePlanning from "@/pages/life/LifePlanning";
import LifeAreas from "@/pages/life/LifeAreas";
import LifeReviews from "@/pages/life/Reviews";
import LifeFaith from "@/pages/life/FaithTracker";
import CommandAI from "@/pages/life/CommandAI";
import WellnessCommand from "@/pages/wellness/Command";
import WellnessPlan from "@/pages/wellness/Plan";
import WellnessGlow from "@/pages/wellness/Glow";
import WellnessProgress from "@/pages/wellness/Progress";
import WellnessReviews from "@/pages/wellness/Reviews";
import WellnessStrategist from "@/pages/wellness/Strategist";
import { CentralStateProvider } from "@/context/CentralStateContext";
import CentralLayout from "@/components/CentralLayout";
import CentralCommand from "@/pages/central/Command";
import CentralReports from "@/pages/central/Reports";
import CentralAI from "@/pages/central/AI";
import CentralDigest from "@/pages/central/Digest";
import CentralPillars from "@/pages/central/Pillars";
import RhythmLayout from "@/components/RhythmLayout";
import { RhythmStateProvider } from "@/context/RhythmStateContext";
import WeeklyReview from "@/pages/rhythm/Review";
import WeeklyPlan from "@/pages/rhythm/Plan";

const Guard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--text-muted)]">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/* Command center chooser */}
      <Route path="/" element={<Guard><CommandSelect /></Guard>} />

      {/* Wealth Command Center */}
      <Route
        path="/wealth"
        element={
          <Guard>
            <FinancialStateProvider>
              <Layout />
            </FinancialStateProvider>
          </Guard>
        }
      >
        <Route index element={<Overview />} />
        <Route path="cash-flow" element={<CashFlow />} />
        <Route path="budget" element={<Budget />} />
        <Route path="life-plan" element={<LifePlan />} />
        <Route path="credit" element={<Credit />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="goals" element={<Goals />} />
        <Route path="purchase-planner" element={<PurchasePlanner />} />
        <Route path="grocery" element={<Grocery />} />
        <Route path="advisor" element={<Advisor />} />
        <Route path="wealth-plan" element={<WealthPlan />} />
        <Route path="protection" element={<Protection />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Life Command Center */}
      <Route
        path="/life"
        element={
          <Guard>
            <LifeStateProvider>
              <LifeLayout />
            </LifeStateProvider>
          </Guard>
        }
      >
        <Route index element={<LifeCommand />} />
        <Route path="planning" element={<LifePlanning />} />
        <Route path="areas" element={<LifeAreas />} />
        <Route path="faith" element={<LifeFaith />} />
        <Route path="reviews" element={<LifeReviews />} />
        <Route path="ai" element={<CommandAI />} />
      </Route>

      {/* Health & Wellness Command Center */}
      <Route
        path="/wellness"
        element={
          <Guard>
            <WellnessStateProvider>
              <WellnessLayout />
            </WellnessStateProvider>
          </Guard>
        }
      >
        <Route index element={<WellnessCommand />} />
        <Route path="plan" element={<WellnessPlan />} />
        <Route path="glow" element={<WellnessGlow />} />
        <Route path="progress" element={<WellnessProgress />} />
        <Route path="reviews" element={<WellnessReviews />} />
        <Route path="ai" element={<WellnessStrategist />} />
      </Route>

      {/* Command Central */}
      <Route
        path="/central"
        element={
          <Guard>
            <CentralStateProvider>
              <CentralLayout />
            </CentralStateProvider>
          </Guard>
        }
      >
        <Route index element={<CentralCommand />} />
        <Route path="pillars" element={<CentralPillars />} />
        <Route path="digest" element={<CentralDigest />} />
        <Route path="reports" element={<CentralReports />} />
        <Route path="ai" element={<CentralAI />} />
      </Route>

      {/* Rhythm Command Center — weekly review & planning */}
      <Route
        path="/rhythm"
        element={
          <Guard>
            <CentralStateProvider>
              <RhythmStateProvider>
                <RhythmLayout />
              </RhythmStateProvider>
            </CentralStateProvider>
          </Guard>
        }
      >
        <Route index element={<WeeklyReview />} />
        <Route path="plan" element={<WeeklyPlan />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PeriodProvider>
            <AppRoutes />
          </PeriodProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
